// ─── Date-range helpers shared by the Jour / Semaine / Mois views ─────────────
// Weeks run Monday → Sunday, as on the POS tablets.

export const CALENDAR_VIEWS = ['day', 'week', 'month'];

export const startOfDay = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};

export const endOfDay = (date) => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
};

export const addDays = (date, n) => {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
};

export const startOfWeek = (date) => {
    const d = startOfDay(date);
    return addDays(d, -((d.getDay() + 6) % 7)); // getDay(): 0 = Sunday
};

export const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const dayKey = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

// The 7 days (Mon → Sun) of the week containing `date`
export const getWeekDays = (date) => {
    const monday = startOfWeek(date);
    return Array.from({length: 7}, (_, i) => addDays(monday, i));
};

// Full month grid: from the Monday on/before the 1st to the Sunday on/after the last day
export const getMonthGridDays = (date) => {
    const first = new Date(date.getFullYear(), date.getMonth(), 1);
    const last = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const days = [];
    for (let d = startOfWeek(first); d <= last || days.length % 7 !== 0; d = addDays(d, 1)) days.push(d);
    return days;
};

// [start, end] fetched from the API for a given view
export const getViewRange = (date, view) => {
    if (view === 'week') {
        const days = getWeekDays(date);
        return [days[0], endOfDay(days[6])];
    }
    if (view === 'month') {
        const days = getMonthGridDays(date);
        return [days[0], endOfDay(days[days.length - 1])];
    }
    return [startOfDay(date), endOfDay(date)];
};

// Previous / next period for the toolbar chevrons
export const shiftDate = (date, view, delta) => {
    if (view === 'month') return new Date(date.getFullYear(), date.getMonth() + delta, 1);
    return addDays(date, view === 'week' ? 7 * delta : delta);
};

export const isCurrentPeriod = (date, view, today = new Date()) => {
    if (view === 'month') return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth();
    if (view === 'week') return isSameDay(startOfWeek(date), startOfWeek(today));
    return isSameDay(date, today);
};

export const formatTime = (date) => date.toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'});

/**
 * Side-by-side layout for the appointments of one day column: events are
 * grouped into clusters of (transitively) overlapping appointments, and each
 * one gets a column inside its cluster. Returns [{event, col, cols}].
 */
export const layoutOverlappingEvents = (events) => {
    const sorted = [...events].sort((a, b) => a.start - b.start || b.end - a.end);
    const result = [];
    let cluster = [];
    let columnEnds = [];
    let clusterEnd = null;

    const flush = () => {
        cluster.forEach(item => result.push({...item, cols: columnEnds.length}));
        cluster = [];
        columnEnds = [];
    };

    sorted.forEach(event => {
        if (clusterEnd !== null && event.start >= clusterEnd) flush();
        let col = columnEnds.findIndex(end => end <= event.start);
        if (col === -1) { col = columnEnds.length; columnEnds.push(event.end); }
        else columnEnds[col] = event.end;
        cluster.push({event, col});
        clusterEnd = clusterEnd === null || cluster.length === 1 ? event.end : Math.max(clusterEnd, event.end);
    });
    flush();
    return result;
};
